/* eslint-disable no-param-reassign */
import { FC } from 'react';
import * as capCells from 'common/constants/cap-table';
import {
  COMPOUNDING_PERIOD_ALIAS,
  COMPOUNDING_PERIOD_TITLE,
  CUMULATIVE_DIVIDENDS_ALIAS,
  CUMULATIVE_DIVIDENDS_TITLE,
  LIQUIDATION_PREFERENCE_ALIAS,
  LIQUIDATION_PREFERENCE_TITLE,
  LPL_EFFECTIVE_DATE_ALIAS,
  LPL_EFFECTIVE_DATE_TITLE,
} from 'common/constants/cap-table';
import { capTableTwoDecimalPercentFormat, capTableXSuffixFormat, xLargeSuffixFormat } from 'common/formats/formats';
import { GridDateProps, Row } from 'common/types/scalarSpreadsheet';
import { GridDate, GridDateEditor, GridDateValueViewer } from 'components/FeaturedSpreadsheet/components';
import {
  LIQ_PREF_LEDGER_ITEM_NAME_ALIAS,
  LIQ_PREF_LEDGER_ITEM_NAME_TITLE,
} from 'pages/CapTable/cap-table/utilities/variablePropertiesUtils';
import {
  BASIS_3_VALUE,
  ISSUE_PRICE_VALUE,
  MULTIPLE_OF_ALIAS,
  MULTIPLE_OF_TITLE,
  YEARFRAC_BASIS_ALIAS,
  YEARFRAC_BASIS_TITLE,
} from './constants';
import { LiquidationPreferenceRowConfigParams } from './liquidation-preference.types';

const liquidationPreferenceLedgerRowConfig = (params: LiquidationPreferenceRowConfigParams) => {
  const { isUsingByExitDate, customTitles = {} } = params;
  const rowConfig = [
    {
      value: LIQ_PREF_LEDGER_ITEM_NAME_TITLE,
      alias: LIQ_PREF_LEDGER_ITEM_NAME_ALIAS,
      hidden: false,
      readOnly: false,
      gridType: 'string',
      dbType: 'string',
      displayUndefined: true,
      isRequired: true,
    },
    {
      value: LIQUIDATION_PREFERENCE_TITLE,
      alias: LIQUIDATION_PREFERENCE_ALIAS,
      defaultValue: '0',
      hidden: false,
      readOnly: false,
      format: capTableXSuffixFormat,
      gridType: 'number',
      dbType: 'string',
      displayUndefined: true,
      isRequired: true,
      minValue: 0,
    },
    {
      value: MULTIPLE_OF_TITLE,
      alias: MULTIPLE_OF_ALIAS,
      defaultValue: ISSUE_PRICE_VALUE,
      dbType: 'number',
      gridType: 'number',
    },
    {
      value: CUMULATIVE_DIVIDENDS_TITLE,
      alias: CUMULATIVE_DIVIDENDS_ALIAS,
      readOnly: false,
      expr: '',
      gridType: 'number',
      dbType: 'boolean',
      displayUndefined: true,
    },
    {
      value: 'Dividend Rate',
      defaultValue: '',
      alias: capCells.DIVIDEND_RATE_ALIAS,
      parent: capCells.PREFERRED_TERMS_ALIAS,
      readOnly: false,
      format: capTableTwoDecimalPercentFormat,
      dbDecimalPlaces: 3,
      expr: '',
      gridType: 'percentage',
      dbType: 'string',
      minValue: 0,
    },
    {
      defaultValue: BASIS_3_VALUE,
      value: YEARFRAC_BASIS_TITLE,
      alias: YEARFRAC_BASIS_ALIAS,
      gridType: 'number',
      dbType: 'number',
    },
    {
      value: capCells.COMPOUNDING_TITLE,
      alias: capCells.COMPOUNDING_ALIAS,
      expr: '',
      gridType: 'number',
      dbType: 'boolean',
      displayUndefined: true,
    },
    {
      value: COMPOUNDING_PERIOD_TITLE,
      alias: COMPOUNDING_PERIOD_ALIAS,
      format: xLargeSuffixFormat,
      expr: '',
      gridType: 'number',
      dbType: 'number',
      allowDecimals: false,
      allowNegativeValue: false,
    },
  ] as Row[];
  if (isUsingByExitDate) {
    // insert effective date row config in position 1

    rowConfig.splice(1, 0, {
      value: LPL_EFFECTIVE_DATE_TITLE,
      alias: LPL_EFFECTIVE_DATE_ALIAS,
      hidden: false,
      readOnly: false,
      gridType: 'gridDate',
      gridDateComponent: GridDate as FC<GridDateProps>,
      dataEditor: (props: any) => <GridDateEditor {...props} />,
      valueViewer: (props: any) => <GridDateValueViewer {...props} slashFormatted />,
      dbType: 'dbDate',
      displayUndefined: true,
      isRequired: true,
    } as Row);
  }

  return rowConfig.map(config => {
    if (config.alias && customTitles[config.alias]) {
      config.value = customTitles[config.alias];
    }
    return config;
  });
};

export default liquidationPreferenceLedgerRowConfig;
